import * as PIXI from 'pixi.js';
import { style, styleSmall, styleTimeSignature, SMALLEST_FRACTION, WIDTH } from '@/constants';
import { EngineAbstract } from '@/components/engine-abstract';
import game from '@/model/game';
import { createText } from '@/ui/menu-builder';
import { mapNote, pitchToHeight, STAFF_FIVE_LINES, G_CLEF, F_CLEF, BAR_BEGIN, BAR_END, mapKey } from '@/util/font-util';

export const ONE_BAR_WIDTH_PX = 448,
    NOTE_WIDTH_PX = ONE_BAR_WIDTH_PX / SMALLEST_FRACTION, //  ONE_BAR_WIDTH_PX / SMALLEST_FRACTION has to be INTeger
    PADDING = 120, SCROLL_WINDOW_PX = PADDING;
const HEIGHT_HALF_STEP = 11;
const BACKGROUND_COLOR = 0x666666;
const LHS_BORDER_COLOR = 0x333333;
const BACKGROUND_OPACITY = 1;

export class NoteText extends PIXI.Text {
    _label = ''; _style = {};
    constructor(label, style) {
        super(label, style);
        this._label = label;
        this._style = style;
    }

    clone() {
        const clone = new NoteText(this._label, this._style);
        clone.x = this.x;
        clone.y = this.y;
        clone.tint = this.tint;
        return clone;
    }
}

export class NotationEngine extends EngineAbstract {

    G_CLEF_Y = 80;
    F_CLEF_Y = this.G_CLEF_Y + 220;
    Y_COORDS = [this.G_CLEF_Y, this.F_CLEF_Y];

    constructor(
        sections,
        key,
        divisionsPerQuarterNote,
        containerWidth,
        containerHeight,
        lhsWidth,
        lhsHeight,
        singleClef = 0 // 0 - both clefs, 1 - G, 2 - F
    ) {

        super();
        this.singleClef = singleClef;
        const notes = [];

        this.height = containerHeight;
        this.width = containerWidth;
        const staticBackground = new PIXI.Graphics();
        staticBackground.height = containerHeight;
        this.addChild(staticBackground);

        const lhs = this.createLhs(lhsWidth, lhsHeight, sections, key);

        this.gx = PADDING,
            this.fx = PADDING;

        sections.forEach((section, barIndex) => {
            // add small barIndex 
            this.placeBarIndex(barIndex, this.gx, this.Y_COORDS[0] - 85);
            this.placeBarIndex(barIndex, this.fx, this.Y_COORDS[1] - 85);

            // draw bar begining
            [1, 2].forEach((staff) => this.placeNote(`${BAR_BEGIN} ${STAFF_FIVE_LINES}`, staff, 1, true, true));

            // section.note can be array, or single obj for one element
            if (section?.note && !section?.note?.forEach) {
                section.note = [section.note];
            }

            // draw staff lines for section first
            this.placeNote(new Array(SMALLEST_FRACTION).join(STAFF_FIVE_LINES), 1, SMALLEST_FRACTION, true, true)
            this.placeNote(new Array(SMALLEST_FRACTION).join(STAFF_FIVE_LINES), 2, SMALLEST_FRACTION, true, true)

            section.note.forEach((note) => {
                // if staff is missing in notes - define staff 
                if (!note.staff) {
                    switch (note.staff) {
                        case "up": note.staff = 1; break;
                        case "down": note.staff = 2; break;
                        default: note.staff = 1; break;
                    }
                }

                // duration here is always base SMALLEST_FRACTION
                // so for SMALLEST_FRACTION = 64, quarter note duration 16
                let duration = note.duration / (divisionsPerQuarterNote * 4) * SMALLEST_FRACTION;
                const isChord = note.hasOwnProperty('chord');
                const noteLabel = this.placeNote(
                    mapNote(note, divisionsPerQuarterNote),
                    note.staff,
                    duration,
                    isChord, isChord);
                const heightIncrement = pitchToHeight(note) * HEIGHT_HALF_STEP;
                noteLabel.y -= heightIncrement;
                notes.push({
                    xmlNote: note,
                    label: noteLabel,
                })
            });

            // fx gotta be === gx
            const max = Math.max(this.fx, this.gx);
            this.fx = max; this.gx = max;

            [1, 2].forEach((staff) => this.placeNote(BAR_END, staff, 1, true, true));
        });

        staticBackground.width = this.width;
        staticBackground.beginFill(BACKGROUND_COLOR, BACKGROUND_OPACITY);
        staticBackground.drawRect(0, 0, this.width, this.height);
        staticBackground.endFill();

        const mask = new PIXI.Sprite(PIXI.Texture.WHITE);
        mask.width = containerWidth;
        mask.height = containerHeight;
        const wrapper = new PIXI.Container();
        wrapper.addChild(this);
        wrapper.mask = mask;
        wrapper.addChild(mask); // make sure mask it added to display list somewhere!

        this.createJudgementLine(containerWidth, containerHeight);
        this.notes = notes;
        this.lhs = lhs;
        this.wrapper = wrapper;
    }

    get clefs() {
        return this.singleClef == 1 ? [G_CLEF] : [G_CLEF, F_CLEF];
    }

    createLhs(lhsWidth, lhsHeight, sections, key) {
        const lhsBackground = new PIXI.Graphics();
        lhsBackground.width = lhsWidth;
        lhsBackground.height = lhsHeight;

        const lhs = new PIXI.Container();
        lhs.addChild(lhsBackground);

        // time and key signatures 
        let { time } = Array.isArray(sections[0]?.attributes) ? sections[0].attributes[0] : sections[0].attributes;
        this.clefs.forEach((clefSign, idx) => {
            const y = this.Y_COORDS[idx];
            const clef = new PIXI.Text(`${clefSign}`, style);
            clef.x = 280;
            clef.y = idx === 0 ? y - 30 : y - 60;
            lhs.addChild(clef);

            const beats = new PIXI.Text(`${time.beats}`, styleTimeSignature);
            const division = new PIXI.Text(`\uE090`, styleTimeSignature);
            const beatType = new PIXI.Text(`${time['beat-type']}`, styleTimeSignature);
            [beats, division, beatType].forEach((label, labelIndex) => {
                label.x = 366;
                label.y = y - 65;
                label.y += labelIndex * 45;
                if (labelIndex === 1) label.y -= 20;

            })
            lhs.addChild(beats);
            lhs.addChild(division);
            lhs.addChild(beatType);

            // lhs key
            const keySymbolSprite = new PIXI.Text(`key: ${mapKey(key)}`, styleTimeSignature);
            keySymbolSprite.position.set(80, 45);
            lhs.addChild(keySymbolSprite);
        });

        lhsBackground.beginFill(BACKGROUND_COLOR, BACKGROUND_OPACITY);
        lhsBackground.drawRect(0, 0, lhsWidth, lhsHeight);
        lhsBackground.endFill();

        lhsBackground.beginFill(LHS_BORDER_COLOR, BACKGROUND_OPACITY);
        lhsBackground.drawRect(lhsWidth - 4, 0, 4, lhsHeight);
        lhsBackground.endFill();
        return lhs;
    }

    createJudgementLine(containerWidth, containerHeight) {
        const wrapper = new PIXI.Container();
        const green = new PIXI.Sprite(PIXI.Texture.WHITE);
        green.width = 40;
        green.x = WIDTH - containerWidth + 120; //lhs pos
        green.y = -40;
        green.height = containerHeight * 1.10;
        green.alpha = 0.5;
        green.tint = 0x00FF00;
        wrapper.addChild(green);
        const timeLabel = createText({
            text: '0.00s'
        });
        wrapper.addChild(timeLabel);
        timeLabel.position.set(
            WIDTH - containerWidth + 120,
            40
        );
        this.addChild(wrapper);
        this.judgementLine = wrapper;
        this.judgementLineTime = timeLabel;
    }

    /** skipShift=true won't increment fx/gx */
    placeNote(text, staff, duration = 1, skipShift = false, isChord = false) {
        const y = this.Y_COORDS[staff - 1];
        const x = (staff === 2 ? this.fx : this.gx);
        const richText = new NoteText(text, style);
        richText.x = x;
        richText.y = y;
        this.addChild(richText);

        if (isChord) {
            // first note of chord has been already placed,
            // so to get previous note X, i go back -duration
            richText.x = x - NOTE_WIDTH_PX * duration;
        }
        if (skipShift == false) {
            if (staff === 2) {
                this.fx += NOTE_WIDTH_PX * duration;
            } else {
                this.gx += NOTE_WIDTH_PX * duration;
            }
        }

        return richText;
    }

    placeBarIndex(idx, x, y) {
        const richText = new PIXI.Text(`${idx}`, styleSmall);
        richText.x = x;
        richText.y = y;
        this.addChild(richText);
    }

    getFields() {
        return { container: this, lhs: this.lhs, wrapper: this.wrapper, noteSprites: this.notes }
    }

    update(currentTime, bpm) {
        if (currentTime <= 0) {
            return;
        }
        const BEATS_PER_SECOND = bpm / 60;
        const beatsPlayed = currentTime * BEATS_PER_SECOND;
        const barsPlayed = beatsPlayed / 4;
        const scrollPosition = barsPlayed * ONE_BAR_WIDTH_PX;
        this.x = SCROLL_WINDOW_PX - scrollPosition;
        this.updateJudgementLine(currentTime, scrollPosition);
    }

    updateJudgementLine(scrollPosition, currentTime) {
        this.judgementLine.position.x = scrollPosition - ONE_BAR_WIDTH_PX;
        // barline = ${Math.trunc(currentTime / (game.beatIntervalSeconds * 4))}
        this.judgementLineTime.text = `${currentTime?.toFixed(2)}s`;
    }
}

